<script lang="ts">
  import type { Item } from '@streepaf/shared'
  import { t } from '../../lib/i18n/index.svelte.js'
  import type { ListStore } from '../../lib/state/listStore.svelte.js'
  import { prefs } from '../../lib/state/prefs.svelte.js'
  import { toasts } from '../../lib/state/toasts.svelte.js'
  import Icon from '../ui/Icon.svelte'
  import IconButton from '../ui/IconButton.svelte'
  import Menu from '../ui/Menu.svelte'
  import MenuItem from '../ui/MenuItem.svelte'
  import Checkbox from './Checkbox.svelte'
  import ItemText from './ItemText.svelte'

  interface Props {
    item: Item
    store: ListStore
    onedit: (id: string, field: 'text' | 'note' | 'quantity' | 'claim') => void
    onmove: (id: string, direction: -1 | 1) => void
  }
  const { item, store, onedit, onmove }: Props = $props()

  let menuOpen = $state(false)
  let editing = $state(false)
  let draft = $state('')
  let field = $state<HTMLTextAreaElement | null>(null)

  const canEdit = $derived(store.canEdit)
  const pulsing = $derived(Date.now() - (store.remoteChanged[item.id] ?? 0) < 900)

  function startEdit(): void {
    if (!canEdit) {
      toasts.show(t('view_only_toast'))
      return
    }
    draft = item.text
    editing = true
    store.editingId = item.id
  }

  function commit(): void {
    const value = draft.trim()
    editing = false
    store.editingId = null
    if (!value) {
      store.deleteItem(item.id)
      return
    }
    if (value !== item.text) store.updateItem(item.id, { text: value })
  }

  function cancel(): void {
    editing = false
    store.editingId = null
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit()
      onedit('', 'text')
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      cancel()
      return
    }
    if (event.key === 'Backspace' && draft === '') {
      event.preventDefault()
      editing = false
      store.editingId = null
      store.deleteItem(item.id, { silent: true })
    }
  }

  function grow(node: HTMLTextAreaElement): void {
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }

  $effect(() => {
    if (editing && field) {
      field.focus()
      field.setSelectionRange(field.value.length, field.value.length)
      grow(field)
    }
  })

  function toggle(checked: boolean): void {
    store.setChecked(item.id, checked, prefs.displayName || undefined)
  }

  function claim(): void {
    if (item.claimedBy) store.updateItem(item.id, { claimedBy: null })
    else onedit(item.id, 'claim')
  }
</script>

{#if item.kind === 'section'}
  <li class="section" class:pulse={pulsing}>
    {#if canEdit}
      <span class="grip" aria-hidden="true"><Icon name="grip" size={18} /></span>
    {/if}
    {#if editing}
      <textarea
        bind:this={field}
        bind:value={draft}
        rows="1"
        onkeydown={onKeydown}
        onblur={commit}
        oninput={(e) => grow(e.currentTarget)}
        aria-label={t('section_placeholder')}
      ></textarea>
    {:else}
      <button class="section-title" onclick={startEdit} disabled={!canEdit}>{item.text}</button>
    {/if}
    {#if canEdit}
      <div class="menu-anchor">
        <IconButton icon="more" label={t('item_menu')} onclick={() => (menuOpen = !menuOpen)} />
        <Menu open={menuOpen} onclose={() => (menuOpen = false)}>
          <MenuItem icon="edit" onclick={() => { menuOpen = false; startEdit() }}>
            {t('item_edit')}
          </MenuItem>
          <MenuItem icon="chevron" onclick={() => { menuOpen = false; onmove(item.id, -1) }}>
            {t('item_move_up')}
          </MenuItem>
          <MenuItem icon="chevron" onclick={() => { menuOpen = false; onmove(item.id, 1) }}>
            {t('item_move_down')}
          </MenuItem>
          <MenuItem icon="trash" tone="danger" onclick={() => { menuOpen = false; store.deleteItem(item.id) }}>
            {t('item_delete')}
          </MenuItem>
        </Menu>
      </div>
    {/if}
  </li>
{:else}
  <li class="row" class:checked={item.checked} class:pulse={pulsing}>
    <Checkbox
      checked={item.checked}
      disabled={!canEdit}
      label={item.text}
      onchange={toggle}
    />

    <div class="content">
      {#if editing}
        <textarea
          bind:this={field}
          bind:value={draft}
          rows="1"
          onkeydown={onKeydown}
          onblur={commit}
          oninput={(e) => grow(e.currentTarget)}
          aria-label={item.text}
        ></textarea>
      {:else}
        <button class="text" onclick={startEdit} disabled={!canEdit}>
          <span class="label"><ItemText text={item.text} /></span>
          {#if item.quantity}<span class="qty">{item.quantity}</span>{/if}
        </button>
      {/if}
      <div class="meta">
        {#if item.note}<span class="note">{item.note}</span>{/if}
        {#if item.claimedBy}
          <button class="claim" onclick={claim} disabled={!canEdit}>{item.claimedBy}</button>
        {/if}
        {#if item.checked && item.checkedBy}
          <span class="by"><Icon name="check" size={12} />{item.checkedBy}</span>
        {/if}
      </div>
    </div>

    {#if canEdit}
      <span class="grip" title={t('drag_handle')} aria-hidden="true"><Icon name="grip" size={18} /></span>
      <div class="menu-anchor">
        <IconButton icon="more" label={t('item_menu')} onclick={() => (menuOpen = !menuOpen)} />
        <Menu open={menuOpen} onclose={() => (menuOpen = false)}>
          <MenuItem icon="edit" onclick={() => { menuOpen = false; startEdit() }}>
            {t('item_edit')}
          </MenuItem>
          <MenuItem icon="list" onclick={() => { menuOpen = false; onedit(item.id, 'note') }}>
            {t('item_note')}
          </MenuItem>
          <MenuItem icon="plus" onclick={() => { menuOpen = false; onedit(item.id, 'quantity') }}>
            {t('item_quantity')}
          </MenuItem>
          <MenuItem icon="sparkle" onclick={() => { menuOpen = false; claim() }}>
            {item.claimedBy ? t('item_claim_release') : t('item_claim')}
          </MenuItem>
          <MenuItem icon="chevron" onclick={() => { menuOpen = false; onmove(item.id, -1) }}>
            {t('item_move_up')}
          </MenuItem>
          <MenuItem icon="chevron" onclick={() => { menuOpen = false; onmove(item.id, 1) }}>
            {t('item_move_down')}
          </MenuItem>
          <MenuItem icon="trash" tone="danger" onclick={() => { menuOpen = false; store.deleteItem(item.id) }}>
            {t('item_delete')}
          </MenuItem>
        </Menu>
      </div>
    {/if}
  </li>
{/if}

<style>
  .row,
  .section {
    display: flex;
    align-items: flex-start;
    gap: var(--space-1);
    min-height: 52px;
    padding-right: var(--space-1);
    border-radius: var(--radius);
    background: var(--surface);
    border-bottom: 1px solid var(--border);
  }
  .row:last-child,
  .section:last-child {
    border-bottom: none;
  }
  .section {
    align-items: center;
    min-height: 44px;
    margin-top: var(--space-4);
    padding-left: var(--space-2);
    background: transparent;
    border-bottom: 2px solid var(--border);
  }
  .section-title {
    flex: 1;
    padding: var(--space-2) var(--space-1);
    border: none;
    background: transparent;
    text-align: left;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--fg-muted);
  }
  .content {
    flex: 1;
    min-width: 0;
    padding: var(--space-2) 0;
  }
  .text {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    width: 100%;
    padding: 4px 0;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 17px;
    line-height: 1.35;
    color: var(--fg);
    overflow-wrap: anywhere;
  }
  .text:disabled {
    cursor: default;
  }
  .label {
    /* The strike is drawn as a gradient so it can animate across the words. */
    background-image: linear-gradient(currentColor, currentColor);
    background-repeat: no-repeat;
    background-position: 0 60%;
    background-size: 0% 1.5px;
    transition: background-size var(--dur) var(--ease);
  }
  .checked .label {
    background-size: 100% 1.5px;
  }
  .checked .text {
    color: var(--fg-faint);
  }
  .qty {
    flex: 0 0 auto;
    padding: 1px var(--space-2);
    border-radius: 999px;
    background: var(--surface-2);
    font-size: 13px;
    font-weight: 600;
    color: var(--fg-muted);
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
    margin-top: 2px;
    font-size: 13px;
    color: var(--fg-muted);
  }
  .note {
    overflow-wrap: anywhere;
  }
  .claim {
    padding: 1px var(--space-2);
    border: none;
    border-radius: 999px;
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 13px;
    font-weight: 600;
  }
  .by {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: var(--fg-faint);
  }
  textarea {
    width: 100%;
    padding: 4px 0;
    border: none;
    background: transparent;
    font-size: 17px;
    line-height: 1.35;
    resize: none;
    overflow: hidden;
  }
  textarea:focus {
    outline: none;
  }
  .grip {
    display: none;
    align-items: center;
    align-self: stretch;
    padding: 0 var(--space-1);
    color: var(--fg-faint);
    cursor: grab;
    touch-action: none;
  }
  .menu-anchor {
    position: relative;
    align-self: center;
  }
  .pulse {
    animation: pulse 900ms var(--ease);
  }
  @keyframes pulse {
    from {
      background-color: var(--accent-soft);
    }
  }
  @media (pointer: coarse) {
    .grip {
      display: flex;
    }
  }
  @media (hover: hover) {
    .row:hover .grip,
    .section:hover .grip {
      display: flex;
    }
  }
</style>
